/*
 * kp-tech-page.js — the technology page's hit-test player (#40).
 *
 *   <kp-tech-hittest>  One well, four panels, four tabs. The shot on show
 *                      starts on arrival and loops, under the same rules as
 *                      the <kp-film> loops above it — muted, because no
 *                      browser lets a page start audio unasked. The sound of
 *                      contact is still the point of the section, so a second
 *                      control turns it on, and that press is the gesture the
 *                      browser wanted. Switching tabs shows another panel and
 *                      carries both states across. A real tablist, arrow keys
 *                      included: unlike the accordion bands this is one well
 *                      with four labels, which is what the pattern is for.
 *
 * The hero film and the dead-feel / good-feel pair used to live here as
 * <kp-tech-film>. They are <kp-film> in kp-film.js now (#47), because the
 * chapter collection hero wanted the same player, and the helpers below come
 * from there. THAT MAKES THE LOAD ORDER A CONTRACT: every section tagging
 * this file must tag kp-film.js first. Both are deferred, so document order
 * is execution order.
 *
 * Wrapped, because every kp-*.js is a classic <script src> and sections on
 * this page tag this file more than once: a bare top-level const would throw
 * "already declared" on the second pass (ticket #22). customElements.get()
 * keeps the define idempotent.
 */
(function () {
  /* From kp-film.js. Missing means the load-order contract above was broken,
   * and it should be loud rather than a player that quietly never starts. */
  const KP_FILM = window.KpFilm;
  if (!KP_FILM) {
    console.error('kp-tech-page.js: kp-film.js must be tagged before it.');
    return;
  }
  const swallow = KP_FILM.swallow;
  const warm = KP_FILM.warm;
  const nearObserver = KP_FILM.nearObserver;
  const KP_TP_REDUCED = KP_FILM.reduced;

  /* ── The hit test: one well, a panel per shot ────────────────────── */

  class KpTechHittest extends HTMLElement {
    connectedCallback() {
      this.panels = Array.from(this.querySelectorAll('[data-kp-thit-panel]'));
      this.toggle = this.querySelector('[data-kp-film-toggle]');
      this.label = this.querySelector('[data-kp-film-label]');
      this.sound = this.querySelector('[data-kp-thit-sound]');
      this.soundLabel = this.querySelector('[data-kp-thit-sound-label]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.panels.length || !this.toggle || !this.tabs.length) return;

      this.videos = this.panels.map((panel) => panel.querySelector('video'));

      /* Muted is the starting state and it is not negotiable: a page cannot
       * begin audio on its own. The sound control is how a visitor buys it,
       * and their press is the gesture the browser was waiting for. */
      this.loud = false;
      this.videos.forEach((video) => {
        if (video) video.muted = true;
      });

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.toggle.addEventListener('click', () => this.onToggle(), { signal });
      /* The picture is the pause. While the film runs the centre mark is
       * hidden (kp-tech-page.css), so a tap anywhere in the well — not on
       * the sound control, not on the mark itself — toggles playback. */
      this.well = this.querySelector('.kp-thit__well');
      if (this.well) {
        this.well.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('button, a')) return;
            this.onToggle();
          },
          { signal }
        );
      }
      if (this.sound) {
        this.sound.addEventListener('click', () => this.onSound(), { signal });
      }
      this.videos.forEach((video) => {
        if (!video) return;
        video.addEventListener('play', () => this.sync(), { signal });
        video.addEventListener('pause', () => this.sync(), { signal });
        video.addEventListener('ended', () => this.sync(), { signal });
      });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', (event) => this.onKey(event, index), { signal });
      });

      const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.index = selected === -1 ? 0 : selected;
      this.paint();

      // Same as the films above: reduced motion means the loop never starts
      // itself, and the control is that visitor's way in.
      this.pausedByHand = KP_TP_REDUCED.matches;
      this.sync();
      this.watch();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      [this.observer, this.near].forEach((o) => {
        if (o) o.disconnect();
      });
    }

    get video() {
      return this.videos[this.index] || null;
    }

    /* Only the shot on show is warmed. Warming all four would be four films'
     * metadata for a visitor who watches one, and the other three are
     * `hidden` — each warms when its tab is picked. */
    watch() {
      this.near = nearObserver(this, [this.video].filter(Boolean));

      if (!('IntersectionObserver' in window)) {
        if (!this.pausedByHand) this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!this.pausedByHand) this.play();
            } else if (this.video) {
              this.video.pause();
            }
          });
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);
    }

    onKey(event, index) {
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;

      event.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }

    /*
     * Shows another shot's panel. Both states cross with it: a visitor who
     * turned the sound on and tapped PRECISE CONTROL asked to hear that one
     * too, and one who had paused stays paused. The film they left keeps its
     * position, so tabbing back resumes rather than restarts.
     */
    select(index) {
      if (index === this.index) return;

      const previous = this.video;
      const wasPlaying = previous && !previous.paused && !previous.ended;
      if (previous) previous.pause();

      this.index = index;
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      this.paint();

      const next = this.video;
      if (next) {
        // The panel was hidden, so this film is still at preload="none".
        warm([next]);
        next.muted = !this.loud;
        if (wasPlaying || !this.pausedByHand) swallow(next.play());
      }
      this.sync();
    }

    paint() {
      this.tabs.forEach((tab, i) => {
        const on = i === this.index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    }

    play() {
      const video = this.video;
      if (!video) return;
      warm([video]);
      video.muted = !this.loud;
      swallow(video.play());
    }

    onToggle() {
      const video = this.video;
      if (!video) return;
      if (video.paused || video.ended) {
        this.pausedByHand = false;
        this.play();
      } else {
        this.pausedByHand = true;
        video.pause();
      }
    }

    /* The press itself is what makes the audio legal, so the unmute happens
     * inside the handler and nowhere else. If the film was paused, turning
     * the sound on is also a request to hear it. */
    onSound() {
      this.loud = !this.loud;
      const video = this.video;
      if (video) {
        video.muted = !this.loud;
        if (this.loud && (video.paused || video.ended)) {
          this.pausedByHand = false;
          this.play();
        }
      }
      this.syncSound();
    }

    syncSound() {
      if (!this.sound) return;
      this.sound.setAttribute('aria-pressed', this.loud ? 'true' : 'false');
      if (this.soundLabel) {
        this.soundLabel.textContent = this.loud
          ? this.sound.dataset.pauseLabel || ''
          : this.sound.dataset.playLabel || '';
      }
    }

    sync() {
      const video = this.video;
      const playing = !!video && !video.paused && !video.ended;
      this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      if (this.label) {
        this.label.textContent = playing
          ? this.toggle.dataset.pauseLabel || ''
          : this.toggle.dataset.playLabel || '';
      }
      this.syncSound();
    }
  }

  if (!customElements.get('kp-tech-hittest')) customElements.define('kp-tech-hittest', KpTechHittest);

})();
